/**
 * Tool Call Panel - Real-time display of tool executions during streaming
 * Shows tool name, parameters, and results as they come from SSE events
 */

import React, { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  Search,
  FileText,
  Calculator,
  Image,
  Terminal,
  Globe,
  Eye,
  ChevronDown,
  ChevronRight,
  Wrench
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ToolCall {
  id: string;
  name: string;
  status: 'pending' | 'completed' | 'error';
  parameters?: any;
  result?: any;
  startedAt?: string;
  completedAt?: string;
}

interface ToolCallPanelProps {
  toolCalls: ToolCall[];
  className?: string;
}

const toolIcons: Record<string, React.ReactNode> = {
  google_web_search: <Search className="h-3.5 w-3.5" />,
  read_file: <FileText className="h-3.5 w-3.5" />,
  Read: <FileText className="h-3.5 w-3.5" />,
  write_file: <FileText className="h-3.5 w-3.5" />,
  Write: <FileText className="h-3.5 w-3.5" />,
  calculator: <Calculator className="h-3.5 w-3.5" />,
  generate_image: <Image className="h-3.5 w-3.5" />,
  shell: <Terminal className="h-3.5 w-3.5" />,
  Bash: <Terminal className="h-3.5 w-3.5" />,
  web_fetch: <Globe className="h-3.5 w-3.5" />,
  WebFetch: <Globe className="h-3.5 w-3.5" />,
  WebSearch: <Search className="h-3.5 w-3.5" />,
  list_directory: <Eye className="h-3.5 w-3.5" />,
  Glob: <Search className="h-3.5 w-3.5" />,
  Grep: <Search className="h-3.5 w-3.5" />,
  Edit: <FileText className="h-3.5 w-3.5" />,
  Task: <Wrench className="h-3.5 w-3.5" />,
  TodoRead: <FileText className="h-3.5 w-3.5" />,
  TodoWrite: <FileText className="h-3.5 w-3.5" />,
};

const getToolIcon = (toolName: string) => {
  return toolIcons[toolName] || <Terminal className="h-3.5 w-3.5" />;
};

const getToolDisplayName = (toolName: string): string => {
  const names: Record<string, string> = {
    google_web_search: 'Web Search',
    read_file: 'Read File',
    Read: 'Read File',
    write_file: 'Write File',
    Write: 'Write File',
    calculator: 'Calculator',
    generate_image: 'Generate Image',
    shell: 'Execute Command',
    Bash: 'Execute Command',
    web_fetch: 'Fetch URL',
    WebFetch: 'Fetch URL',
    WebSearch: 'Web Search',
    list_directory: 'List Directory',
    Glob: 'Find Files',
    Grep: 'Search Content',
    Edit: 'Edit File',
    Task: 'Run Task',
    TodoRead: 'Read Todos',
    TodoWrite: 'Write Todos',
  };
  return names[toolName] || toolName;
};

const formatParameters = (params: any): string => {
  if (!params) return '';

  // Handle common parameter patterns
  if (typeof params === 'string') return params;

  // Extract meaningful parameter for display
  if (params.query) return `"${truncate(params.query, 40)}"`;
  if (params.file_path) return truncate(params.file_path, 50);
  if (params.path) return truncate(params.path, 50);
  if (params.pattern) return `pattern: "${truncate(params.pattern, 30)}"`;
  if (params.command) return truncate(params.command, 50);
  if (params.url) return truncate(params.url, 50);
  if (params.prompt) return `"${truncate(params.prompt, 40)}"`;

  // Fallback: stringify first few keys
  const keys = Object.keys(params).slice(0, 2);
  if (keys.length === 0) return '';
  return keys.map(k => `${k}: ${truncate(String(params[k]), 20)}`).join(', ');
};

const formatResult = (result: any): string => {
  if (!result) return '';
  if (typeof result === 'string') return truncate(result, 100);
  if (result.content) return truncate(String(result.content), 100);
  if (result.output) return truncate(String(result.output), 100);
  if (result.text) return truncate(String(result.text), 100);
  if (result.message) return truncate(String(result.message), 100);

  // Try to stringify
  try {
    const str = JSON.stringify(result);
    return truncate(str, 100);
  } catch {
    return '[Result]';
  }
};

const truncate = (str: string, maxLen: number): string => {
  if (!str) return '';
  const cleaned = str.replace(/\n/g, ' ').trim();
  if (cleaned.length <= maxLen) return cleaned;
  return cleaned.substring(0, maxLen) + '...';
};

const calculateDuration = (startedAt?: string, completedAt?: string): string | null => {
  if (!startedAt) return null;

  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationMs = end - start;

  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(1)}s`;
};

export function ToolCallPanel({ toolCalls, className }: ToolCallPanelProps) {
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const completedCount = useMemo(() =>
    toolCalls.filter(tc => tc.status === 'completed').length,
    [toolCalls]
  );

  const allCompleted = completedCount === toolCalls.length && toolCalls.length > 0;
  const hasPending = toolCalls.some(tc => tc.status === 'pending');

  const toggleExpanded = (id: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  if (toolCalls.length === 0) {
    return null;
  }

  return (
    <Card className={cn(
      "p-2 border",
      hasPending ? "bg-accent/50 border-primary/30" : "bg-gray-50/50 border-gray-200",
      className
    )}>
      <div className="space-y-1.5">
        {/* Header */}
        <div className="flex items-center gap-2 text-xs">
          {hasPending ? (
            <Loader2 className="h-3 w-3 animate-spin text-primary" />
          ) : allCompleted ? (
            <CheckCircle className="h-3 w-3 text-green-600" />
          ) : (
            <Wrench className="h-3 w-3 text-gray-500" />
          )}
          <span className={cn(
            "font-medium",
            hasPending ? "text-accent-foreground" : "text-gray-700"
          )}>
            {hasPending ? 'Using Tools' : 'Tools Used'}
          </span>
          <Badge
            variant="secondary"
            className={cn(
              "text-[10px] px-1.5 py-0 h-4",
              hasPending ? "bg-accent text-accent-foreground" : "bg-gray-100 text-gray-600"
            )}
          >
            {completedCount}/{toolCalls.length}
          </Badge>
        </div>

        {/* Tool list */}
        <div className="space-y-1">
          {toolCalls.map((tool) => {
            const isExpanded = expandedTools.has(tool.id);
            const duration = calculateDuration(tool.startedAt, tool.completedAt);
            const params = formatParameters(tool.parameters);
            const result = formatResult(tool.result);
            const hasDetails = params || result;

            return (
              <div
                key={tool.id}
                className={cn(
                  "rounded border transition-colors",
                  tool.status === 'pending'
                    ? "bg-white border-primary/30"
                    : tool.status === 'error'
                    ? "bg-red-50 border-red-200"
                    : "bg-white border-gray-200"
                )}
              >
                {/* Tool header row */}
                <div
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 text-xs",
                    hasDetails && "cursor-pointer hover:bg-gray-50"
                  )}
                  onClick={() => hasDetails && toggleExpanded(tool.id)}
                >
                  {/* Status icon */}
                  <div className="flex-shrink-0">
                    {tool.status === 'pending' ? (
                      <Loader2 className="h-3 w-3 animate-spin text-primary" />
                    ) : tool.status === 'error' ? (
                      <AlertCircle className="h-3 w-3 text-red-500" />
                    ) : (
                      <CheckCircle className="h-3 w-3 text-green-500" />
                    )}
                  </div>

                  {/* Tool icon */}
                  <div className={cn(
                    "flex-shrink-0",
                    tool.status === 'pending' ? "text-primary" : "text-gray-500"
                  )}>
                    {getToolIcon(tool.name)}
                  </div>

                  {/* Tool name */}
                  <span className={cn(
                    "font-medium",
                    tool.status === 'pending' ? "text-accent-foreground" : "text-gray-700"
                  )}>
                    {getToolDisplayName(tool.name)}
                  </span>

                  {/* Brief params preview (when collapsed) */}
                  {!isExpanded && params && (
                    <span className="text-gray-500 truncate flex-1 text-[11px]">
                      {params}
                    </span>
                  )}

                  {/* Duration */}
                  {duration && (
                    <span className="text-[10px] text-gray-400 flex-shrink-0">
                      {duration}
                    </span>
                  )}

                  {/* Expand/collapse indicator */}
                  {hasDetails && (
                    <div className="flex-shrink-0 text-gray-400">
                      {isExpanded ? (
                        <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronRight className="h-3 w-3" />
                      )}
                    </div>
                  )}
                </div>

                {/* Expanded details */}
                {isExpanded && hasDetails && (
                  <div className="px-2 pb-2 pt-1 border-t border-gray-100 space-y-1.5">
                    {params && (
                      <div className="text-[11px]">
                        <span className="text-gray-500">Input: </span>
                        <span className="text-gray-700 font-mono">{params}</span>
                      </div>
                    )}
                    {result && (
                      <div className="text-[11px]">
                        <span className="text-gray-500">Result: </span>
                        <span className="text-gray-700">{result}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
